'use client';

import Script from 'next/script';
import { useEnvContext } from 'next-runtime-env';

import { useGeoContext } from '@/app/contexts/GeoProvider';
import logToDatadog from '@/logger/logToDatadog';

const KLARNA_CLIENT_IDS = {
  DE: 'NEXT_PUBLIC_KLARNA_CLIENT_ID_DE',
  GB: 'NEXT_PUBLIC_KLARNA_CLIENT_ID_UK',
} as const;

type KlarnaSupportedCountry = keyof typeof KLARNA_CLIENT_IDS;

const KlarnaScript = () => {
  const { countryData, isGeoReady } = useGeoContext();
  const env = useEnvContext();
  const countryCode = countryData?.countryCode;
  const isProduction = env['NEXT_PUBLIC_OURA_ENV'] === 'production';

  if (!isGeoReady || !countryCode || !(countryCode in KLARNA_CLIENT_IDS)) {
    return null;
  }
  // Get client ID for this country
  const envKey = KLARNA_CLIENT_IDS[countryCode as KlarnaSupportedCountry];
  const clientId = env[envKey];

  // Don't render if no client ID is configured
  if (!clientId) {
    return null;
  }

  const klarnaEnv = isProduction ? 'production' : 'playground';

  return (
    <Script
      async
      data-env={klarnaEnv}
      data-client-id={clientId}
      id="klarna-script"
      onError={(error) => {
        logToDatadog({
          error: error,
          message: 'Klarna script failed to load',
          module: 'app',
        });
      }}
      src="https://js.klarna.com/web-sdk/v1/klarna.js"
      strategy="afterInteractive"
    />
  );
};

export default KlarnaScript;
